import { useState, useEffect, type ReactNode } from "react";
import {
  PickupLocationContext,
  type PickupLocationContextType,
} from "./PickupLocationContext";
import type { DayOption } from "../utils/types";
import { callCreatePickupLocation, callDeletePickupLocation, callFetchPickupLocations, callUpdatePickupLocation, PickupLocation } from "../../../api/pickupLocations";

interface PickupLocationProviderProps {
  children: ReactNode;
}

export const PickupLocationContextProvider = ({
  children,
}: PickupLocationProviderProps) => {
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPickupLocations = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const pickupLocations: PickupLocation[] = await callFetchPickupLocations();

      setPickupLocations(pickupLocations);
    } catch (err) {
      console.error("Error fetching pickup locations:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch pickup locations");
    } finally {
      setLoading(false);
    }
  };

  const createPickupLocation = async (
    name: string,
    address: string,
    latitude: number,
    longitude: number,
    days: DayOption[],
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const createdPickupLocation: PickupLocation = await callCreatePickupLocation({
        name: name,
        address: address,
        latitude: latitude,
        longitude: longitude,
        days: days,
      });

      setPickupLocations((prev) => [...prev, createdPickupLocation]);
    } catch (err) {
      console.error("Error adding pickup location:", err);
      setError(err instanceof Error ? err.message : "Failed to add pickup location");
    } finally {
      setLoading(false);
    }
  };

  const updatePickupLocation = async (
    id: string,
    name?: string,
    address?: string,
    latitude?: number,
    longitude?: number,
    days?: DayOption[],
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const updatedPickupLocation: PickupLocation = await callUpdatePickupLocation(id, {
        name: name,
        address: address,
        latitude: latitude,
        longitude: longitude,
        days: days,
      });

      // Update the location in local state
      setPickupLocations((prev) =>
        prev.map((location) =>
          location._id === id
            ? {
              ...updatedPickupLocation,
              updatedAt: new Date().toISOString(),
            }
            : location,
        ),
      );
    } catch (err) {
      console.error("Error updating pickup location:", err);
      setError(err instanceof Error ? err.message : "Failed to update pickup location");
    } finally {
      setLoading(false);
    }
  };

  const deletePickupLocation = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await callDeletePickupLocation(id);

      // Remove the location from local state
      setPickupLocations((prev) => prev.filter((location) => location._id !== id));
    } catch (err) {
      console.error("Error deleting pickup location:", err);
      setError(err instanceof Error ? err.message : "Failed to delete pickup location");
    } finally {
      setLoading(false);
    }
  };

  const refreshPickupLocations = async (): Promise<void> => {
    await fetchPickupLocations();
  };

  // Fetch pickup locations on component mount
  useEffect(() => {
    fetchPickupLocations();
  }, []);

  const value: PickupLocationContextType = {
    pickupLocations,
    loading,
    error,
    fetchPickupLocations,
    createPickupLocation,
    updatePickupLocation,
    deletePickupLocation,
    refreshPickupLocations,
  };

  return (
    <PickupLocationContext.Provider value={value}>
      {children}
    </PickupLocationContext.Provider>
  );
};
